import { toast } from 'sonner'
import { CanvasBackgroundContextMenu } from '@/components/app/editor/context-menus/canvas-background-context-menu'
import { ObjectContextMenu } from '@/components/app/editor/context-menus/object-context-menu'
import { PhotoContextMenu } from '@/components/app/editor/context-menus/photo-context-menu'
import type { MenuActions } from '@/components/app/editor/context-menus/menu-action-rows'
import type {
  ObjectActionRowId,
  ObjectMenuActionId,
} from '@/components/app/editor/context-menus/object-context-menu'
import type { PhotoMenuActionId } from '@/components/app/editor/context-menus/photo-context-menu'
import { TextInlineEditContextMenu } from '@/components/app/editor/context-menus/text-inline-edit-context-menu'
import { isSingleFrozenSelectionForUnfreeze } from '@/components/app/editor/select-all-policy'
import { lockedObjectBlocksCopy } from '@/lib/editor/clipboard-paste'
import { partitionSelectionForCut } from '@/lib/editor/cut-selection-policy'
import { selectionIncludesSelfFrozenForDelete } from '@/lib/editor/freeze-object-messages'
import {
  isUngroupUnavailable,
  selectionIncludesGroupShape,
  tryUngroupSelection,
} from '@/lib/groups/ungroup-selection'
import { BACKGROUNDS_RIGHT_PANEL_ID, requestRightPanelOpen } from '@/lib/panels/right-panel-events'
import { usePhotoPanelContext } from '@/hooks/use-photo-panel-context'
import { isShapeTextFrame } from '@/lib/shapes/shape-text-frame'
import { duplicateSelection } from '../handlers/duplicate-selection'
import type { CanvasArrangeCommand } from '../helpers/canvas-key-action'
import type {
  CanvasEditModes,
  CanvasFoundation,
  CanvasSelectionCommands,
  CanvasViewport,
} from '../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../types/resolved-center-canvas-config'

/**
 * The canvas context menus: the background menu (right-click on empty canvas), the photo menu (a
 * single photo selected) or the object menu (any other selection), and the inline shape-text edit
 * menu. Every gate is read live off the ShapeManager each render.
 */
export function CanvasContextMenus({
  config,
  foundation,
  editModes,
  viewport,
  selectionCommands,
}: {
  config: Pick<
    ResolvedCenterCanvasConfig,
    | 'linkedTextChains'
    | 'photoEffectsOpen'
    | 'onPhotoEffectsOpenChange'
    | 'onCloseEditor'
    | 'onActivateSelectTool'
    | 'onSelectAllApplied'
    | 'onSelectNoneApplied'
    | 'onEditTextPath'
    | 'onTextOnPath'
    | 'onLayerReorder'
    | 'onUnifiedLayerArrange'
    | 'onDeleteShapesHandled'
    | 'onAddCaption'
    | 'onRemovePhotoFromShape'
    | 'onRemoveBackgroundFromShape'
    | 'isRemovingBackgroundShapeId'
    | 'onCheckSpelling'
    | 'onConvertTextToShape'
  >
  foundation: Pick<
    CanvasFoundation,
    | 'contextMenus'
    | 'scrollRef'
    | 'internalShapeManagerRef'
    | 'linkedTextChainsRef'
    | 'canUnifiedBringForward'
    | 'canUnifiedSendBackward'
    | 'getArrangeSelectedIds'
    | 'backgroundPlacement'
  >
  editModes: Pick<
    CanvasEditModes,
    | 'lockFreezeCommands'
    | 'clearUnifiedSelectionWithHistory'
    | 'tryEnterTextOnPathMode'
    | 'tryEnterCropMode'
    | 'activeIsolationGroupId'
  >
  viewport: Pick<CanvasViewport, 'getCanvasPointFromPointerEvent'>
  selectionCommands: Pick<
    CanvasSelectionCommands,
    | 'pasteFromClipboard'
    | 'isTextEditSessionActive'
    | 'hasPasteCandidate'
    | 'copySelectionToClipboard'
    | 'cutSelectionToClipboard'
    | 'getCurrentSelectionSnapshots'
    | 'selectAllForActiveTextEdit'
    | 'deleteSelectedObjects'
  >
}) {
  const {
    linkedTextChains,
    photoEffectsOpen,
    onPhotoEffectsOpenChange,
    onCloseEditor,
    onActivateSelectTool,
    onSelectAllApplied,
    onSelectNoneApplied,
    onEditTextPath,
    onTextOnPath,
    onLayerReorder,
    onUnifiedLayerArrange,
    onDeleteShapesHandled,
    onAddCaption,
    onRemovePhotoFromShape,
    onRemoveBackgroundFromShape,
    isRemovingBackgroundShapeId,
    onCheckSpelling,
    onConvertTextToShape,
  } = config
  const {
    contextMenus: menus,
    scrollRef,
    internalShapeManagerRef,
    linkedTextChainsRef,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    getArrangeSelectedIds,
    backgroundPlacement,
  } = foundation
  const {
    lockFreezeCommands: lockFreeze,
    clearUnifiedSelectionWithHistory,
    tryEnterTextOnPathMode,
    tryEnterCropMode,
    activeIsolationGroupId,
  } = editModes
  const { getCanvasPointFromPointerEvent } = viewport
  const {
    pasteFromClipboard,
    isTextEditSessionActive,
    hasPasteCandidate,
    copySelectionToClipboard,
    cutSelectionToClipboard,
    getCurrentSelectionSnapshots,
    selectAllForActiveTextEdit,
    deleteSelectedObjects,
  } = selectionCommands
  const {
    objectContextMenu,
    shapeTextEditContextMenu,
    setShapeTextEditContextMenu,
    handleObjectContextMenuClose,
    contextMenuSelectedObjectCount,
    contextMenuHasAnyLockedObjects,
    contextMenuHasAnyFrozenObjects,
    contextMenuSelectionFullyLocked,
    contextMenuSingleFrozenSelection,
    contextMenuSelectionHasAnyFrozen,
    contextMenuSelectionHasAnyLocked,
    contextMenuTargetIsGroup,
    contextMenuSelectionHasLinkedTextFrame,
    contextMenuConvertTextToShapeVisible,
    contextMenuTextOnPathTarget,
    contextMenuSelectionCopyBlockedByLock,
    contextMenuCutBlocked,
  } = menus
  const {
    lockSelectedObjects,
    unlockSelectedObjects,
    freezeSelectedObjects,
    unfreezeSelectedObjects,
    unlockAllObjects,
    unfreezeAllObjects,
  } = lockFreeze

  /** The menu's target shape: the right-clicked one, else the first selected. */
  const resolveMenuTargetId = () =>
    objectContextMenu.targetShapeId ?? objectContextMenu.selectedShapeIds[0]

  // The same rules the Photo Actions panel and the photo toolbar use, so the three surfaces agree on
  // which actions are available. Locked is folded in here; the menu adds only its own frozen gates.
  // Only while the photo menu is open: closing it keeps the old target in state, and passing it on
  // would keep that photo's queries and placement subscription alive for nothing.
  const menuTargetId = resolveMenuTargetId()
  const menuTargetShape = menuTargetId
    ? internalShapeManagerRef.current?.getShapes().find((s) => s.id === menuTargetId)
    : undefined
  const { isActionDisabled: isPhotoActionDisabled } = usePhotoPanelContext(
    objectContextMenu.open && objectContextMenu.isPhotoSelection ? menuTargetShape : undefined,
    {
      interactionLocked: contextMenuSelectionHasAnyLocked,
      isRemovingBackground: Boolean(menuTargetId && isRemovingBackgroundShapeId?.(menuTargetId)),
    }
  )

  /** Photo menu "Clipping Path" / object menu "Edit Path": node-edit the target's path. */
  const editTargetPath = onEditTextPath
    ? () => {
        const id = resolveMenuTargetId()

        if (id) onEditTextPath(id)
      }
    : undefined

  const enterTextOnPathForTarget = contextMenuTextOnPathTarget
    ? () => {
        // `onTextOnPath` (from home.tsx) stays supported for callers that only want to
        // observe the command; the mode itself is canvas-local, like crop/node-edit.
        tryEnterTextOnPathMode(contextMenuTextOnPathTarget)
        onTextOnPath?.()
      }
    : undefined

  /** A z-order command from the photo or object menu, applied to the live selection. */
  const arrangeSelection = (command: CanvasArrangeCommand) => {
    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length) {
      const result = mgr![command]()

      if (result && onLayerReorder) onLayerReorder(result.beforeOrder, result.afterOrder)
      onUnifiedLayerArrange?.(selectedShapeIds, command)
    }
  }

  /** Delete, unless the selection is locked or frozen itself (then the row is hidden). */
  const resolveDeleteSelection = () => {
    const { shapeSnapshots, textSnapshots } = getCurrentSelectionSnapshots()
    const hasLockedSelection =
      shapeSnapshots.some((shape) => shape.locked === true) ||
      textSnapshots.some((box) => box.locked === true)
    const hasSelfFrozenSelection = selectionIncludesSelfFrozenForDelete(
      shapeSnapshots,
      textSnapshots
    )

    if (hasLockedSelection || hasSelfFrozenSelection) return undefined
    return () => {
      const deleteResult = deleteSelectedObjects()

      if (deleteResult === 'deleted') {
        onDeleteShapesHandled?.()
      }
    }
  }

  const hasNoLockedShapes = () =>
    !(internalShapeManagerRef.current?.getShapes() ?? []).some((s) => s.locked)
  const hasNoFrozenShapes = () =>
    !(internalShapeManagerRef.current?.getShapes() ?? []).some((s) => s.frozen)

  /** Photo and object menus: Duplicate — the same helper as Ctrl/Cmd+D and Edit → Duplicate. */
  const duplicateFromMenu = () => {
    duplicateSelection({ isTextEditSessionActive, copySelectionToClipboard, pasteFromClipboard })
  }

  /** Object menu: the Copy row's gate, which Duplicate shares. Reads the live selection. */
  const objectCopyDisabled = () => {
    const { shapeSnapshots } = getCurrentSelectionSnapshots()

    if (shapeSnapshots.length === 0) return true
    // Copy-only carve-out for locked linked frames — see `lockedObjectBlocksCopy`.
    // The `cut` gate deliberately keeps the plain locked block.
    return shapeSnapshots.some((shape) => lockedObjectBlocksCopy(shape, linkedTextChains))
  }

  /**
   * Photo and object menus: the shared arrange, group, clipboard and lock/freeze actions. Cut and
   * Copy are gated differently per menu, so the caller passes those two; Duplicate shares Copy's.
   * Ungroup is object-menu only and added there.
   */
  const sharedObjectActions = (clipboardDisabled: {
    cut: boolean
    copy: boolean
  }): MenuActions<ObjectActionRowId> => {
    const mgr = internalShapeManagerRef.current
    const selectedCount = mgr?.getSelectedShapeIds()?.length ?? 0
    // z-order rows, gated on the unified layer order.
    const cannotBringForward =
      !canUnifiedBringForward(getArrangeSelectedIds()) || contextMenuSelectionHasAnyLocked
    const cannotSendBackward =
      !canUnifiedSendBackward(getArrangeSelectedIds()) || contextMenuSelectionHasAnyLocked
    const pasteDisabled = !hasPasteCandidate() || contextMenuSelectionHasAnyLocked

    return {
      bringToFront: { run: () => arrangeSelection('bringToFront'), disabled: cannotBringForward },
      bringForward: { run: () => arrangeSelection('bringForward'), disabled: cannotBringForward },
      sendBackward: { run: () => arrangeSelection('sendBackward'), disabled: cannotSendBackward },
      sendToBack: { run: () => arrangeSelection('sendToBack'), disabled: cannotSendBackward },
      groupObjects: {
        run: () => {
          internalShapeManagerRef.current?.groupSelectedObjects()
        },
        disabled: !mgr?.canGroupSelectedObjects(),
      },
      cut: {
        run: () => {
          cutSelectionToClipboard()
        },
        disabled: clipboardDisabled.cut,
      },
      copy: {
        run: () => {
          copySelectionToClipboard()
        },
        disabled: clipboardDisabled.copy,
      },
      duplicate: { run: duplicateFromMenu, disabled: clipboardDisabled.copy },
      paste: {
        run: () => {
          pasteFromClipboard(false)
        },
        disabled: pasteDisabled,
      },
      pasteInPlace: {
        run: () => {
          pasteFromClipboard(true)
        },
        disabled: pasteDisabled,
      },
      delete: { run: resolveDeleteSelection() },
      // Lock and Unlock swap, as do Freeze and Unfreeze: a fully locked selection offers Unlock,
      // and a frozen one hides Lock (and vice versa for Freeze).
      unlockSelected: {
        run: () => {
          unlockSelectedObjects()
        },
        disabled: selectedCount === 0,
        hidden: !contextMenuSelectionFullyLocked,
      },
      lockSelected: {
        run: () => {
          lockSelectedObjects()
        },
        disabled:
          activeIsolationGroupId != null || selectedCount === 0 || contextMenuSelectionHasAnyFrozen,
        hidden: contextMenuSelectionFullyLocked || contextMenuSelectionHasAnyFrozen,
      },
      unfreezeSelected: {
        run: () => {
          unfreezeSelectedObjects()
        },
        disabled:
          activeIsolationGroupId != null ||
          !isSingleFrozenSelectionForUnfreeze(
            (mgr?.getShapes() ?? []).filter((shape) =>
              (mgr?.getSelectedShapeIds() ?? []).includes(shape.id)
            ),
            []
          ),
        hidden: !contextMenuSingleFrozenSelection,
      },
      freezeSelected: {
        run: () => {
          freezeSelectedObjects()
        },
        disabled:
          activeIsolationGroupId != null || selectedCount === 0 || contextMenuSelectionHasAnyLocked,
        hidden: contextMenuSingleFrozenSelection || contextMenuSelectionHasAnyLocked,
      },
      unlockAll: {
        run: () => {
          unlockAllObjects()
        },
        disabled: hasNoLockedShapes(),
        hidden: !contextMenuHasAnyLockedObjects,
      },
      unfreezeAll: {
        run: () => {
          unfreezeAllObjects()
        },
        disabled: hasNoFrozenShapes(),
        hidden: !contextMenuHasAnyFrozenObjects,
      },
    }
  }

  const photoMenuActions = (): MenuActions<PhotoMenuActionId> => ({
    ...sharedObjectActions({
      cut: contextMenuCutBlocked,
      copy: contextMenuSelectionCopyBlockedByLock,
    }),
    editPhoto: { disabled: isPhotoActionDisabled('editPhoto') },
    photoEffects: {
      run: onPhotoEffectsOpenChange ? () => onPhotoEffectsOpenChange(!photoEffectsOpen) : undefined,
      disabled: isPhotoActionDisabled('photoEffects'),
    },
    cropPhoto: {
      run: () => {
        const id = resolveMenuTargetId()
        const shape = id
          ? internalShapeManagerRef.current?.getShapes().find((s) => s.id === id)
          : undefined

        if (shape) tryEnterCropMode(shape)
      },
      disabled: isPhotoActionDisabled('cropPhoto') || contextMenuSelectionHasAnyFrozen,
    },
    tagPhoto: { disabled: isPhotoActionDisabled('tagPhoto') },
    clippingPath: {
      run: objectContextMenu.selectedShapeIds.length > 0 ? editTargetPath : undefined,
      disabled:
        isPhotoActionDisabled('clippingPath') ||
        contextMenuSelectionHasAnyFrozen ||
        contextMenuSelectedObjectCount > 1,
    },
    removeBackground: {
      run: onRemoveBackgroundFromShape
        ? () => {
            const shapeId = resolveMenuTargetId()

            if (shapeId) onRemoveBackgroundFromShape(shapeId)
          }
        : undefined,
      disabled: isPhotoActionDisabled('removeBackground'),
    },
    removePhoto: {
      run: onRemovePhotoFromShape
        ? () => {
            const shapeId = resolveMenuTargetId()

            if (shapeId) onRemovePhotoFromShape(shapeId)
          }
        : undefined,
      disabled: isPhotoActionDisabled('removePhoto'),
    },
    caption: { run: onAddCaption, disabled: isPhotoActionDisabled('caption') },
    // Locked only, NOT frozen (WMPCM-7632): a frozen object may have Text on a Path applied and
    // edited, because freeze blocks geometry gestures and Text on a Path is a content edit. This
    // also settles a pre-existing split — the Object menu's own gate (`textOnPathDisabledForMenus`,
    // home.tsx) never checked frozen, so the two entry points AC-1 names disagreed. Locked stays
    // blocked (WMPCM-9562 AC-9).
    textOnPath: { run: enterTextOnPathForTarget, disabled: contextMenuSelectionHasAnyLocked },
  })

  const objectMenuActions = (): MenuActions<ObjectMenuActionId> => {
    const mgr = internalShapeManagerRef.current

    return {
      ...sharedObjectActions({
        // Reads the live selection rather than the context-menu snapshot, matching the Copy row.
        // Enabled while anything is cuttable, so a mixed chain's partial cut stays reachable
        // (WMPCM-8663).
        cut: (() => {
          const { shapeSnapshots } = getCurrentSelectionSnapshots()

          return (
            partitionSelectionForCut({
              shapes: shapeSnapshots,
              textBoxes: [],
              chains: linkedTextChainsRef.current,
            }).kind !== 'proceed'
          )
        })(),
        copy: objectCopyDisabled(),
      }),
      ungroupObjects: {
        run: (() => {
          if (!mgr) return undefined

          const shapes = mgr.getShapes()
          const selectedRoots = mgr.getSelectedShapeIds()

          if (!selectionIncludesGroupShape(selectedRoots, shapes)) return undefined

          return () => {
            tryUngroupSelection(mgr, (message) => toast.error(message))
          }
        })(),
        disabled: isUngroupUnavailable(mgr ?? null),
      },
      // Right-click a text frame → "Edit Text". The row only renders when `run` is set, so leaving
      // it unset silently dropped the affordance: the gate used to be "exactly one TextManager box
      // selected", which can never hold now that a text frame is a shape. The capability itself
      // never went away (double-click and the Type tool both reach `enterShapeTextEdit`) — only
      // this entry point did.
      //
      // A LOCKED frame keeps the row, disabled (WMPCM-10578 #4): dropping it reshuffled every row
      // below it relative to the unlocked and frozen menus.
      editText: {
        run: (() => {
          const ids = mgr?.getSelectedShapeIds() ?? []

          if (ids.length !== 1) return undefined
          const shape = mgr?.getShapes().find((s) => s.id === ids[0])

          if (!shape || !isShapeTextFrame(shape)) return undefined

          return () => mgr?.enterShapeTextEdit(ids[0])
        })(),
        disabled: contextMenuSelectionHasAnyLocked,
      },
      // Check Spelling never renders (showTextObjectCommands is always false — a text-box-only
      // selection can no longer exist). Convert text to shape renders on its own gate now; see
      // `contextMenuConvertTextToShapeVisible` (WMPCM-9190, WMPCM-10220).
      checkSpelling: { run: onCheckSpelling, disabled: contextMenuSelectionHasAnyLocked },
      // Never disabled while visible: `contextMenuConvertTextToShapeVisible` already guarantees at
      // least one eligible member, and `handleConvertTextToShape` converts every eligible member
      // of the selection while skipping the rest (with a toast for a size/length failure) — a
      // locked or frozen sibling elsewhere in the selection must not gray out converting the ones
      // that ARE eligible (WMPCM-10220).
      convertTextToShape: {
        run: contextMenuConvertTextToShapeVisible ? onConvertTextToShape : undefined,
        disabled: false,
      },
      editPath: {
        run: editTargetPath,
        disabled:
          contextMenuSelectionHasAnyLocked ||
          contextMenuSelectionHasAnyFrozen ||
          contextMenuSelectionHasLinkedTextFrame ||
          contextMenuTargetIsGroup ||
          // Edit Path node-edits one shape; with several selected it would silently pick one.
          contextMenuSelectedObjectCount > 1,
      },
      // Locked only, NOT frozen (WMPCM-7632) — see the photo menu's actions for why. Note this
      // deliberately diverges from `editPath` directly above it: node editing IS a geometry edit
      // and stays frozen-blocked, Text on a Path is not.
      textOnPath: { run: enterTextOnPathForTarget, disabled: contextMenuSelectionHasAnyLocked },
    }
  }

  return (
    <>
      {objectContextMenu.mode === 'canvas' ? (
        <CanvasBackgroundContextMenu
          open={objectContextMenu.open}
          position={objectContextMenu.position}
          viewportBoundsRef={scrollRef}
          onOpenChange={(open) => {
            if (!open) handleObjectContextMenuClose()
          }}
          onClose={() => {
            onCloseEditor?.()
          }}
          onPaste={(pos) => {
            pasteFromClipboard(false, getCanvasPointFromPointerEvent(pos.x, pos.y))
          }}
          onPasteInPlace={() => {
            pasteFromClipboard(true)
          }}
          onSelectAll={() => {
            // One rule in one place (WMPCM-6867): text-edit mode wins wherever Select All is
            // reached from. This menu opens on empty canvas, so it is the least likely of the four
            // routes to fire mid-edit — but it must not be the one that disagrees.
            if (selectAllForActiveTextEdit()) return

            onActivateSelectTool?.()
            internalShapeManagerRef.current?.selectAll()
            onSelectAllApplied?.()
          }}
          onSelectNone={() => {
            clearUnifiedSelectionWithHistory()
            onSelectNoneApplied?.()
          }}
          onUnlockAll={() => {
            unlockAllObjects()
          }}
          onUnfreezeAll={() => {
            unfreezeAllObjects()
          }}
          showUnlockAll={contextMenuHasAnyLockedObjects}
          showUnfreezeAll={contextMenuHasAnyFrozenObjects}
          onAdjustBackground={() => requestRightPanelOpen(BACKGROUNDS_RIGHT_PANEL_ID)}
          showAdjustBackground={
            backgroundPlacement != null && !backgroundPlacement.isBackgroundsPanelOpen
          }
          disabledItems={{
            paste: !hasPasteCandidate(),
            pasteInPlace: !hasPasteCandidate(),
            selectNone: contextMenuSelectedObjectCount === 0,
            unlockAll: hasNoLockedShapes(),
            unfreezeAll: hasNoFrozenShapes(),
          }}
        />
      ) : objectContextMenu.isPhotoSelection ? (
        <PhotoContextMenu
          open={objectContextMenu.open}
          position={objectContextMenu.position}
          viewportBoundsRef={scrollRef}
          onOpenChange={(open) => {
            if (!open) handleObjectContextMenuClose()
          }}
          actions={photoMenuActions()}
        />
      ) : (
        <ObjectContextMenu
          open={objectContextMenu.open}
          position={objectContextMenu.position}
          viewportBoundsRef={scrollRef}
          showTextObjectCommands={false}
          showPathCommands={objectContextMenu.selectedShapeIds.length > 0}
          onOpenChange={(open) => {
            if (!open) handleObjectContextMenuClose()
          }}
          actions={objectMenuActions()}
        />
      )}
      <TextInlineEditContextMenu
        open={shapeTextEditContextMenu.open}
        position={shapeTextEditContextMenu.position}
        viewportBoundsRef={scrollRef}
        selectionCollapsed={shapeTextEditContextMenu.isCollapsed}
        pastePlainDisabled={false}
        onOpenChange={(open) => {
          if (!open) {
            setShapeTextEditContextMenu((prev) => ({ ...prev, open: false }))
          }
        }}
        onCut={() => {
          internalShapeManagerRef.current?.execShapeTextCut()
        }}
        onCopy={() => {
          internalShapeManagerRef.current?.execShapeTextCopy()
        }}
        onPaste={() => {
          void internalShapeManagerRef.current?.execShapeTextPaste()
        }}
        // Parity with the text-box inline-edit menu above, which has had Select All all along
        // (WMPCM-9070). Same routing as Ctrl+A: a chain member selects the whole story across
        // every member, a solo frame selects its own text.
        onSelectAll={selectAllForActiveTextEdit}
      />
    </>
  )
}
